import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import { VideoPlayer, VideoPlayerRef } from '../../../components/VideoPlayer';
import { RecordingOverlay } from '../../../components/RecordingOverlay';
import { useCourseCapsule } from '../hooks/useCourseCapsule';
import { CourseVideoControls } from '../components/CourseVideoControls';
import { PracticeMode } from '../components/PracticeMode';
import { useRecorder } from '../../../hooks/useRecorder';
import { useSubmissions } from '../../../hooks/useSubmissions';
import { uploadRecording } from '../../../lib/uploadRecording';
import { useAuth } from '../../../hooks/useAuth';
import {
  CourseLayerName,
  COURSE_LAYER_ORDER,
  COURSE_LAYER_LABELS,
  getCourseLayerVideoUrl,
  buildCourseSubmissionSlug,
  hasPracticeMaterials,
} from '../../../types/course';

type ViewTab = CourseLayerName | 'practice';

export function CourseCapsuleViewer() {
  const { courseSlug, capsuleSlug } = useParams<{ courseSlug: string; capsuleSlug: string }>();
  const [searchParams] = useSearchParams();
  const { course, capsule, loading, error } = useCourseCapsule(courseSlug ?? null, capsuleSlug ?? null);
  const { isAuthenticated } = useAuth();

  // Tab selection (layers + practice mode)
  const initialTab = (searchParams.get('layer') as ViewTab) || 'chords';
  const [selectedTab, setSelectedTab] = useState<ViewTab>(initialTab);

  // Video
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Measure header height for viewport calc
  const headerRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const [videoMaxHeight, setVideoMaxHeight] = useState('70vh');

  // Recording
  const [recordingMode, setRecordingMode] = useState(searchParams.get('record') === 'true');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const recorder = useRecorder();
  const { submitRecording } = useSubmissions();

  // Calculate available height so video + controls stay above the fold
  useEffect(() => {
    const calculate = () => {
      const headerH = headerRef.current?.offsetHeight ?? 56;
      const topBarH = topBarRef.current?.offsetHeight ?? 80;
      const controlsH = 52; // playback bar height
      const padding = 24; // top+bottom spacing
      const available = window.innerHeight - headerH - topBarH - controlsH - padding;
      setVideoMaxHeight(`${Math.max(200, available)}px`);
    };
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  // Track play state for overlay button
  useEffect(() => {
    if (!videoElement) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    videoElement.addEventListener('play', onPlay);
    videoElement.addEventListener('pause', onPause);
    setIsPlaying(!videoElement.paused);
    return () => {
      videoElement.removeEventListener('play', onPlay);
      videoElement.removeEventListener('pause', onPause);
    };
  }, [videoElement]);

  const handleVideoReady = useCallback((video: HTMLVideoElement) => {
    setVideoElement(video);
  }, []);

  const togglePlay = useCallback(() => {
    if (!videoElement) return;
    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }, [videoElement]);

  const handleRecordClick = useCallback(() => {
    setRecordingMode(true);
  }, []);

  const handleRecordClose = useCallback(() => {
    setRecordingMode(false);
    recorder.clearRecording();
  }, [recorder]);

  const handleSubmitRecording = useCallback(async (blob: Blob) => {
    if (!courseSlug || !capsuleSlug) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const compoundSlug = buildCourseSubmissionSlug(courseSlug, capsuleSlug);
      const result = await uploadRecording(blob, compoundSlug, (progress) => {
        setUploadProgress(progress.percent);
      });
      await submitRecording(compoundSlug, selectedTab, result.secureUrl);
      setRecordingMode(false);
      recorder.clearRecording();
    } catch (err) {
      console.error('Failed to submit recording:', err);
    } finally {
      setIsUploading(false);
    }
  }, [courseSlug, capsuleSlug, selectedTab, recorder, submitRecording]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="container" style={{ padding: 'var(--space-2xl)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </>
    );
  }

  if (error || !course || !capsule) {
    return (
      <>
        <Header />
        <div className="container" style={{ padding: 'var(--space-2xl)', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-md)' }}>{error || 'Not found'}</h2>
          <Link to={courseSlug ? `/courses/${courseSlug}` : '/courses'} style={{ color: 'var(--color-accent)' }}>
            Back to course
          </Link>
        </div>
      </>
    );
  }

  const videoUrl = selectedTab !== 'practice' ? getCourseLayerVideoUrl(capsule, selectedTab as CourseLayerName) : null;
  const introContent = capsule.layers.intro;
  const availableLayers = COURSE_LAYER_ORDER.filter((l) => capsule.layers[l] !== undefined);
  const showPracticeTab = hasPracticeMaterials(capsule);

  return (
    <>
      <div ref={headerRef}>
        <Header />
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-sm)', paddingBottom: 'var(--space-md)' }}>
        {/* Top bar: breadcrumb + title + tabs */}
        <div ref={topBarRef}>
          <div className="chip-breadcrumb" style={{ marginBottom: 'var(--space-xs)', fontSize: 13 }}>
            <Link to="/courses">קורסים</Link>
            {' / '}
            <Link to={`/courses/${course.slug}`}>{course.title}</Link>
            {' / '}
            {capsule.title}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>{capsule.title}</h1>
            {capsule.artist && (
              <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>— {capsule.artist}</span>
            )}
            {/* Tabs inline — reversed order: תרגול leftmost, הקדמה rightmost */}
            <div className="tabs" style={{ marginLeft: 'auto' }}>
              {showPracticeTab && (
                <button
                  className={`tab ${selectedTab === 'practice' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('practice')}
                >
                  תרגול
                </button>
              )}
              {[...availableLayers].reverse().map((layer) => (
                <button
                  key={layer}
                  className={`tab ${selectedTab === layer ? 'active' : ''}`}
                  onClick={() => setSelectedTab(layer)}
                >
                  {COURSE_LAYER_LABELS[layer]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Intro view */}
        {selectedTab === 'intro' && introContent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            {introContent.description && (
              <div style={{
                padding: 'var(--space-lg)',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                fontSize: 15,
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}>
                {introContent.description}
              </div>
            )}
            {introContent.video && (
              <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', maxHeight: videoMaxHeight }}>
                <VideoPlayer
                  ref={videoPlayerRef}
                  src={introContent.video}
                  onVideoReady={handleVideoReady}
                  showControls
                  hidePanControls
                />
              </div>
            )}
          </div>
        )}

        {/* Chords video view */}
        {selectedTab === 'chords' && videoUrl && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Video container with overlay play button */}
            <div style={{
              position: 'relative',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              maxHeight: videoMaxHeight,
              background: '#000',
              cursor: 'pointer',
            }}
              onClick={togglePlay}
            >
              <VideoPlayer
                ref={videoPlayerRef}
                src={videoUrl}
                onVideoReady={handleVideoReady}
                hidePanControls
                style={{ maxHeight: videoMaxHeight }}
              />
              {/* Big center play/pause overlay */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  opacity: isPlaying ? 0 : 1,
                  transition: 'opacity 0.25s ease',
                }}
              >
                <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                    <polygon points="6,3 20,12 6,21" />
                  </svg>
                </div>
              </div>
            </div>
            {/* Controls bar */}
            <div onClick={(e) => e.stopPropagation()}>
              <CourseVideoControls
                videoElement={videoElement}
                showRecordButton={isAuthenticated && !recordingMode}
                onRecordClick={handleRecordClick}
              />
            </div>
          </div>
        )}

        {/* Practice mode */}
        {selectedTab === 'practice' && (
          <PracticeMode
            pdfUrl={capsule.layers.chords?.chordsPdf}
            pdfFileName={capsule.layers.chords?.originalChordsPdfFileName}
            audioUrl={capsule.layers.chords?.melodyAudio}
            audioFileName={capsule.layers.chords?.originalMelodyAudioFileName}
            showRecordButton={isAuthenticated && !recordingMode}
            onRecordClick={handleRecordClick}
            recordingSlot={recordingMode ? (
              <div style={{ marginTop: 'var(--space-sm)' }}>
                <RecordingOverlay
                  isRecording={recorder.isRecording}
                  recordingUrl={recorder.recordingUrl}
                  recordingBlob={recorder.recordingBlob}
                  error={recorder.error}
                  onStartRecording={recorder.startRecording}
                  onStopRecording={recorder.stopRecording}
                  onClearRecording={recorder.clearRecording}
                  onSubmit={handleSubmitRecording}
                  onClose={handleRecordClose}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  stream={recorder.stream}
                  hasVideo={recorder.hasVideo}
                />
              </div>
            ) : undefined}
          />
        )}

        {/* No video fallback */}
        {selectedTab !== 'intro' && selectedTab !== 'practice' && !videoUrl && (
          <div style={{
            padding: 'var(--space-2xl)',
            textAlign: 'center',
            color: 'var(--color-text-muted)',
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
          }}>
            No video available for this layer yet.
          </div>
        )}

        {/* Recording overlay (chords tab only — practice tab has its own via recordingSlot) */}
        {recordingMode && selectedTab !== 'practice' && (
          <div style={{ marginTop: 'var(--space-md)' }}>
            <RecordingOverlay
              isRecording={recorder.isRecording}
              recordingUrl={recorder.recordingUrl}
              recordingBlob={recorder.recordingBlob}
              error={recorder.error}
              onStartRecording={recorder.startRecording}
              onStopRecording={recorder.stopRecording}
              onClearRecording={recorder.clearRecording}
              onSubmit={handleSubmitRecording}
              onClose={handleRecordClose}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              stream={recorder.stream}
              hasVideo={recorder.hasVideo}
            />
          </div>
        )}
      </div>
    </>
  );
}
